export type FixOutcome =
  | "repointed"
  | "not-fully-resolved"
  | "rejected-no-firm-ref"
  | "rejected-inactive-merge"
  | "rejected-duplicate-merge"
  | "rejected-org-not-broken"
  | "rejected-current-company-mismatch"
  | string;

export type OutcomeState = "done" | "needs-attention" | "error";

const REFUSAL_OUTCOMES: ReadonlySet<string> = new Set([
  "not-fully-resolved",
  "rejected-no-firm-ref",
  "rejected-inactive-merge",
  "rejected-duplicate-merge",
  "rejected-org-not-broken",
  "rejected-current-company-mismatch",
]);

export function mapOutcomeToState(outcome: FixOutcome): OutcomeState {
  if (outcome === "repointed") {
    return "done";
  }
  if (REFUSAL_OUTCOMES.has(outcome)) {
    return "needs-attention";
  }
  return "error";
}

export type FirmRefGroup = {
  firmRef: string | null;
  firmName?: string;
  count?: number;
  class?: string;
};

export function isGroupSelectable(firmRef: string | null): boolean {
  return firmRef !== null && firmRef !== "";
}

export function filterOutBlankGroups<T extends FirmRefGroup>(groups: T[]): T[] {
  return groups.filter(group => isGroupSelectable(group.firmRef));
}

export type ReassignPayload = {
  userId: string;
  firmRef: string;
  firmName: string;
  fromFirmRefs: string[];
};

export function buildReassignPayload(params: {
  userId: string;
  firmRef: string;
  firmName: string;
  selectedFirmRefs: (string | null)[];
}): ReassignPayload | null {
  const fromFirmRefs = params.selectedFirmRefs.filter(
    (ref): ref is string => ref !== null && ref !== "",
  );

  if (fromFirmRefs.length === 0) {
    return null;
  }

  return {
    userId: params.userId,
    firmRef: params.firmRef,
    firmName: params.firmName,
    fromFirmRefs,
  };
}

export type PrecheckTargetUser = {
  externalId?: string | null;
  accountsId?: string | null;
};

export function getPrecheckTargetId(user: PrecheckTargetUser): string | null {
  return user.externalId != null && user.externalId !== "" ? user.externalId : null;
}

// 2026-07-27 rework: the console's tables are now informational only — a fix is performed from a modal
// opened on the row, never from a control inside the row itself (remediation-console.6, .11). These
// header lists are the tabs' single source of truth for their column headers, so this stays enforceable
// rather than a name a reviewer has to remember to check.
export const BROKEN_ACCOUNTS_COLUMN_HEADERS: readonly string[] = [
  "Name",
  "Classification",
  "Status",
  "Affected Users",
  "Live Entitlements",
  "Result",
];

export const ORPHANED_SUBMISSIONS_COLUMN_HEADERS: readonly string[] = [
  "Firm Ref",
  "Count",
  "Class",
];

const ROW_ACTION_HEADERS: ReadonlySet<string> = new Set([
  "actions",
  "action",
  "fix",
  "re-point",
  "repoint",
  "reassign",
  "select",
]);

export function hasRowLevelFixAction(headers: readonly string[]): boolean {
  return headers.some(header => ROW_ACTION_HEADERS.has(header.trim().toLowerCase()));
}

// Design review (2026-07-27, staff-ui-account-remediation-org-details-modal): mapOutcomeToState drives
// the badge colour, but the raw outcome string was what a human actually read next to it — an API enum
// value, not copy. This maps every outcome to a plain sentence saying what happened and, for a refusal,
// what to do next (remediation-console.12). The raw code stays available as secondary debug text — this
// function only replaces the PRIMARY text, never the code itself.
export type OutcomeMessageContext = {
  organisationName?: string;
  companyName?: string;
};

const OUTCOME_SENTENCES: Readonly<Record<string, (subject: string, target: string) => string>> = {
  repointed: (_subject, target) => `Re-pointed to ${target}.`,
  "rejected-inactive-merge": subject =>
    `${subject} is inactive, likely a retired duplicate. It requires a merge, not a re-point. The company you selected is valid.`,
  "rejected-duplicate-merge": () =>
    "This company is already linked to another account. The two accounts require a merge.",
  "rejected-no-firm-ref": (_subject, target) =>
    `${target} has no firm reference and cannot be used as a target. Select a different company.`,
  "rejected-org-not-broken": subject =>
    `${subject} already resolves correctly. No action required.`,
  "rejected-current-company-mismatch": subject =>
    `${subject} was changed by someone else. Reload and try again.`,
  "not-fully-resolved": subject =>
    `No changes were made. ${subject} has no members or no access rights, so a re-point would not restore it.`,
};

export function mapOutcomeToSentence(outcome: FixOutcome, context?: OutcomeMessageContext): string {
  const subject = context?.organisationName ?? "This organisation";
  const target = context?.companyName ?? "The selected company";
  const sentence = OUTCOME_SENTENCES[outcome];

  return sentence
    ? sentence(subject, target)
    : "Unrecognised response. Report the outcome code to engineering.";
}

export type HeldCompanyCandidate = {
  held: boolean;
  organisation?: {
    id: string;
    name: string;
    status: string;
  };
};

export function isCompanyAlreadyHeld(candidate: HeldCompanyCandidate): boolean {
  return candidate.held;
}

export function buildHeldCompanyRefusal(params: {
  companyName: string;
  holder: { name: string; status: string };
}): string {
  const { companyName, holder } = params;

  if (holder.status === "active") {
    return `${companyName} is valid but already linked to ${holder.name}, which is active. These two accounts require a merge.`;
  }

  return `${companyName} is valid but already linked to ${holder.name}, which is inactive. There is nothing to merge; the link must be released first.`;
}

export type DuplicateSignalSource = "grouped-company" | "name-match";

export type DuplicateSignal = {
  source: DuplicateSignalSource;
  detail: string;
};

export type DuplicateIndication = {
  isPossibleDuplicate: boolean;
  message: string;
  signals: DuplicateSignal[];
};

export function combineDuplicateSignals(
  signals: (DuplicateSignal | null | undefined)[],
): DuplicateIndication {
  const presentSignals = signals.filter((signal): signal is DuplicateSignal => signal != null);

  if (presentSignals.length === 0) {
    return { isPossibleDuplicate: false, message: "", signals: [] };
  }

  return {
    isPossibleDuplicate: true,
    message: "Possible duplicate. Check before re-pointing.",
    signals: presentSignals,
  };
}

// A FAILED REQUEST (non-2xx) is a distinct failure shape from a fix OUTCOME (HTTP 200 carrying a refusal
// string, handled by mapOutcomeToSentence above). @legal-500/http's sendError always puts the specific
// reason in `details` (the raw Zod issues array) alongside a generic `error` string like "Validation
// failed" — the UI was showing only the generic string and discarding the reason (remediation-console.13).
export type RequestFailureDetail = {
  path?: (string | number)[];
  message: string;
};

export type RequestFailureLike = {
  message: string;
  details?: RequestFailureDetail[];
};

export function formatRequestFailure(error: RequestFailureLike): string {
  if (error.details && error.details.length > 0) {
    const [detail] = error.details;
    const field = detail.path && detail.path.length > 0 ? detail.path.join(".") : undefined;
    return field
      ? `Re-point failed. ${field}: ${detail.message}`
      : `Re-point failed. ${detail.message}`;
  }

  return `Re-point failed. ${error.message}`;
}
